import type { DirectoryEntry, DirectoryKind } from "@estelle/domain";

import type { Projection } from "@/modules/types";

/**
 * An entry flattened to the field names `DIRECTORY_FIELDS` declares.
 *
 * Computed from the row alone, as every screen's projection is — read the
 * comment at the top of `modules/orders/projections.ts`. The server has already
 * joined a contact's phones and addresses and a customer's orders into the
 * entry, so this is a rename, not a lookup.
 *
 * `phone` and `email` come out empty when the row has none *and* when the
 * server did not send them (a customer, without `customers.view_contact`) — the
 * screen cannot tell the two apart, which is the point of not sending them.
 *
 * `typeIds` and `isArchived` are a contact's own. A customer is empty there
 * rather than "No": it is not archivable and has no type, so a dash is the
 * truth and "No" would be a claim.
 */
export function projectDirectoryEntry(entry: DirectoryEntry): Projection {
  const contact = entry.kind === "contact";
  return {
    displayName: entry.displayName,
    kind: entry.kind,
    companyName: entry.companyName ?? "",
    phone: entry.phone ?? "",
    email: entry.email ?? "",
    city: entry.city ?? "",
    state: entry.state ?? "",
    gstin: entry.gstin ?? "",
    tags: entry.tags,
    typeIds: entry.typeIds,
    isArchived: contact ? entry.isArchived : "",
    createdAt: entry.createdAt,
    // A customer's figures (#637); a contact has no orders, so a dash.
    ordersCount: entry.ordersCount,
    totalSpent: entry.totalSpent,
    lastOrderAt: entry.lastOrderAt,
  };
}

/**
 * What identifies a row — and what the URL's `?record=` says is open.
 *
 * A contact's id and a customer's are separate sequences, so `12` alone does not
 * say whose. A contact keeps its bare id, so a link to a contact is the link it
 * has always been; a customer's is `customer:12`.
 */
export function recordKey(entry: Pick<DirectoryEntry, "kind" | "id">): string {
  return entry.kind === "customer" ? `customer:${entry.id}` : entry.id;
}

/** The kind and id a `?record=` value names; anything unprefixed is a contact. */
export function parseRecordKey(key: string): {
  kind: DirectoryKind;
  id: string;
} {
  return key.startsWith("customer:")
    ? { kind: "customer", id: key.slice("customer:".length) }
    : { kind: "contact", id: key };
}
